import { GraphqlAPI } from "../../core/graphql-api";
import { COLLABORATOR_PERMISSIONS, ROLE_PERMISSIONS } from "../../core/constants";
import { SET_CACHE } from "../store/cacheLayer";
import { setApplicationState } from "../../store/applicationState";
import axios from "axios";
import { logEvent,GA_EVENT_NAMES } from "../../plugins/googleAnalytics";

const graphqlServer = import.meta.env.VITE_GRAPHQL_SERVER_V1;
export const PROJECT_CONSTANT = {
  PROJECT_TYPES: {
    ALL: "ALL",
    PERSONAL: "PERSONAL",
    SHARED_WITH_ME: "SHARED_WITH_ME",
  },
  PROJECTS_PER_REQUEST: 10,
};
export default class ProjectService {
  constructor() {
    this._graphqlAPIService = null;
    this.getProjects = this.getProjects.bind(this);
    this.getProject = this.getProject.bind(this);
    this.uploadDocument = this.uploadDocument.bind(this);
  }

  // Built lazily on first use rather than in the constructor. Several
  // composables (useProjectsList.js, useProjectsPanel.js, useProjectsByTab.js,
  // useDocs.js, ...) hold a ProjectService as a module-level singleton,
  // constructed as soon as that module is first imported — on a fresh login,
  // that can happen before the router guard's `await $auth0.getUser()` has
  // set localStorage.accessToken, permanently baking a missing/stale token
  // into the underlying Apollo client and causing a 401 on the first request
  // (a refresh "fixes" it only because a valid token is already in
  // localStorage from the prior session by the time the module re-evaluates).
  // Reading the token here instead means it's read at first actual API call —
  // which only happens once the app has mounted past the auth guard.
  get graphqlAPIService() {
    if (!this._graphqlAPIService) {
      this.accessToken = localStorage.getItem("accessToken");
      this._graphqlAPIService = new GraphqlAPI(graphqlServer, this.accessToken);
    }
    return this._graphqlAPIService;
  }

  async getProjects(filters = {}) {
    const {
      projectType = PROJECT_CONSTANT.PROJECT_TYPES.ALL,
      projectsPageSize = PROJECT_CONSTANT.PROJECTS_PER_REQUEST,
      projectsCursor = null,
      sortBy = "LAST_MODIFIED",
      sortDirection = "DESC",
    } = filters;
    if (projectType === PROJECT_CONSTANT.PROJECT_TYPES.ALL) {
      const data = await this.graphqlAPIService.getProjects_v4({
        projectsPageSize,
        projectsCursor,
        sortBy,
        sortDirection,
      });
      return formatGetProjectsResponse(data);
    }
  }
  async getProject(filters = {}) {
    try {
      const { projectId = null } = filters;
      if (projectId === null) return [];
      const data = await this.graphqlAPIService.getProject_v4({
        projectId,
      });
      localStorage.setItem("tenantId", data.project?.tenantId || null);
      const formattedData = formatGetProjectResponse(data);
      // await SET_CACHE.cacheProjects({projects: [formattedData]});
      return formattedData;
    } catch (error) {
      console.error("getProject failed:", error);
      return [];
    }
  }
  async createProject(payload = {}) {
    const { name, description = "" } = payload;
    const data = await this.graphqlAPIService.createProject_v4({
      name,
      description,
    });
    logEvent(GA_EVENT_NAMES.PROJECT_CREATED, {  });
    return data;
  }
  async editProject(filters) {
    const { projectId, name, description } = filters;
    return this.graphqlAPIService.updateProjectTitle(
      projectId,
      name,
      description
    );
  }
  async deleteProject(projectId) {
    if (!projectId)
      return { deleted: false, message: "Could not find the Project Id" };
    SET_CACHE.setCurrentProjectLoadingState();
    const response = await this.graphqlAPIService.deleteProject_v4(projectId);
    const { deleted = true, message } = response;
    if (deleted) {
      try {
        SET_CACHE.deleteCachedProject(projectId);
      } catch (error) {}
    }
    return { deleted, message };
  }

  async duplicateProject(projectId) {
    return this.graphqlAPIService.duplicateProject(projectId);
  }

  async getProjectCollaborators(filter) {
    const { projectId, endCursor = null, pageSize = 20 } = filter;
    try {
      const {
        project: {
          collaborators: { edges = [], pageInfo },
          publicStatus = "PUBLIC",
          sessionUserRole = "VIEWER",
        },
      } = await this.graphqlAPIService.getProjectCollaborators_v4({
        projectId,
        endCursor,
        pageSize,
      });
      const collaboratorList = [];
      edges.forEach((edge) => {
        const {
          node: {
            collaboratorRole,
            user: { email, name, profileStatus },
            lastAccessedAt,
          },
        } = edge;
        collaboratorList.push({
          email,
          name,
          role: collaboratorRole,
          profileStatus,
          lastAccessedAt,
        });
      });
      return {
        collaborators: collaboratorList,
        isOwner: sessionUserRole === "OWNER",
        publicStatus,
        pageInfo,
      };
    } catch (error) {
      return {
        collaborators: [],
        isOwner: false,
      };
    }
  }
  async shareProject(payload) {
    const { projectId, collaboratorEmail, collaboratorRole } = payload;
    logEvent(GA_EVENT_NAMES.SHARE_INITIATED, {  });
    return await this.graphqlAPIService.shareProject_v4({
      projectId,
      collaboratorEmail,
      collaboratorRole,
    });
  }

  async updateProjectVisibility(payload) {
    const { projectId, publicStatus } = payload;
    return await this.graphqlAPIService.updateProjectVisibility_v4({
      projectId,
      publicStatus,
    });
  }

  async uploadDocumentMp(payload) {
    console.log("Uploading multipart");
    logEvent(GA_EVENT_NAMES.DOCUMENT_UPLOADED, {  });
    return await this.graphqlAPIService.uploadDocumentMp(payload);
  }
  async uploadDocument(payload) {
    return await this.graphqlAPIService.uploadDocument_v4(payload);
  }

  async getUploadStatus() {
    return await this.graphqlAPIService.getUploadStatus();
  }

  async updateDocumentAnnotationDocId(payload) {
    const { projectId, documentId, annotationDocumentId } = payload;
    return await this.graphqlAPIService.updateDocumentAnnotationDocId(
      projectId,
      documentId,
      annotationDocumentId
    );
  }

  async getDocuments(payload) {
    const {
      projectId,
      pageSize = 20,
      endCursor: cursor = null,
      sortBy = "DATE_ADDED",
      sortDirection = "ASC",
    } = payload;
    try {
      const res = await this.graphqlAPIService.getDocuments({
        projectId,
        pageSize,
        cursor,
        sortBy,
        sortDirection,
      });
      return formatGetDocumentResponse({ ...res, projectId });
    } catch (error) {
      return { documents: [], hasNextPage: false, endCursor: null };
    }
  }

  async getRecentDocuments() {
    try {
      const res = await this.graphqlAPIService.getRecentDocuments();
      return { documents: res?.getRecentDocuments?.documents ?? [] };
    } catch (error) {
      return { documents: [] };
    }
  }

  async getStaredDocuments() {
    try {
      const res = await this.graphqlAPIService.getStaredDocuments();
      return { documents: res?.getStaredDocuments?.documents ?? [] };
    } catch (error) {
      return { documents: [] };
    }
  }

  // "Docs" section — standalone documents with no real project, each backed
  // by an auto-created singleton project (one project, exactly one document).
  async getSingletonProjects(payload = {}) {
    const { first = 30, sortBy = "DATE_ADDED", sortDirection = "DESC" } = payload;
    try {
      const res = await this.graphqlAPIService.getSingletonProjects({ first, sortBy, sortDirection });
      return formatSingletonProjectsResponse(res);
    } catch (error) {
      return { documents: [], hasNextPage: false };
    }
  }

  async getDocument(payload) {
    const { documentId } = payload;
    try {
      const { document = {} } = await this.graphqlAPIService.getDocumentData({
        documentId,
      });
      const { preview } = document;
      // preview is a page-thumbnail image (same field/shape the list queries
      // return as image/png, e.g. formatGetProjectsResponse) — not the PDF
      // itself, so it must be labeled as an image to render in an <img> tag.
      const previewContent = preview ? `data:image/png;base64,${preview}` : "";

      return { ...document, preview: previewContent, rawContent: preview };
    } catch (error) {
      return {};
    }
  }

  async getDocumentStatus(payload) {
    const { documentId } = payload;
    try {
      const { documentImportStatus } = await this.graphqlAPIService.getDocumentStatus({
        documentId,
      });
      return documentImportStatus;
    } catch (error) {
      return {};
    }
  }

  async getDocumentAnnotationDocId(payload) {
    const { documentId } = payload;
    try {
      const res = await this.graphqlAPIService.getDocumentAnnotationDocId(
        documentId
      );
      return res;
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  async downloadDocument(payload) {
    try {
      const { documentId, flatten, original = false } = payload;
      if (original) {
        const {
          downloadSourceDocument: { content = null, originMimeType } = {},
        } = await this.graphqlAPIService.downloadSourceDocument_v4({
          ...payload,
        });
        return { content, originMimeType };
      } else {
        return await this.downloadAnnotatedDocument({ documentId, flatten });
      }
    } catch (error) {
      return { content: null };
    }
  }

  async downloadDocumentForPDFJS(payload) {
    try {
      const { downloadDocument: { content = null } = {} } =
        await this.graphqlAPIService.downloadDocument_v4({ ...payload });
      return { content: `data:application/pdf;base64,${content}` };
    } catch (error) {
      return { content: null };
    }
  }
  async downloadAnnotatedDocument(payload) {
    const { documentId, flatten = false } = payload;
    try {
      const {
        downloadAnnotatedDocumentContent: {
          content = null,
          originMimeType,
        } = {},
      } = await this.graphqlAPIService.downloadAnnotatedDocumentContent({
        documentId,
        flatten,
      });
      if (content === null) {
        throw { error: "File content is null" };
      }
      return { content, originMimeType };
    } catch (error) {
      console.log("download error", error);
      throw { error: "server error downloading your file" };
    }
  }

  async deleteDocument(payload) {
    const { documentId, projectId } = payload;
    try {
      const response = await this.graphqlAPIService.deleteDocument_v4({
        documentId,
        projectId,
      });
      // make local db changes
      SET_CACHE.deleteCachedDocument(documentId);
      return true;
    } catch (error) {
      console.log("error", error);
      return false;
    }
  }

  async copyDocumentToProject(payload) {
    const {
      documentId,
      sourceProjectId,
      targetProjectId,
      withAnnotations = true,
    } = payload;
    try {
      const response = await this.graphqlAPIService.copyDocument2AnotherProject(
        documentId,
        sourceProjectId,
        targetProjectId,
        withAnnotations
      );
      return true;
    } catch (error) {
      console.log("error", error);
      return false;
    }
  }

  async updateDocumentName(payload) {
    const { documentId, name, projectId } = payload;
    try {
      await this.graphqlAPIService.updateDocumentTitle(
        projectId,
        documentId,
        name
      );
      return true;
    } catch (error) {
      console.log("error", error);
      return false;
    }
  }

  async MarkDocumentFavorite(payload) {
    const { documentId, tenantId, email, isFavorite } = payload;
    try {
      const response = await this.graphqlAPIService.markDocumentFavorite_v4({
        documentId,
        tenantId,
        email,
        isFavorite,
      });
      setApplicationState.setFavoriteDocumentChange({
        docId: documentId,
        isFavorite,
      });
      return true;
    } catch (error) {
      console.log("error", error);
      return false;
    }
  }

  async removeFromRecents(payload) {
    const { documentId } = payload;
    await this.graphqlAPIService.removeFromRecents({ documentId });
  }

  async getExecutiveSummaryData(payload) {
    try {
      const { projectId = null, invoke = false } = payload;
      const {
        getExecutiveSummary: { summary = "", pending = false, lastAdded } = {},
      } = await this.graphqlAPIService.getExecutiveSummary({
        projectId,
        invoke,
      });
      return { isPending: pending, summary, lastAdded };
    } catch (error) {
      return { isPending: false, summary: "" };
    }
  }

  async getCloudStorages() {
    try {
      return await this.graphqlAPIService.getCloudStorages();
    } catch (error) {
      console.log("Error in cloud storage fetch query...");
    }
  }

  async downloadDocumentForPDFJSV2({ documentId }) {
    try {
      const baseUrl = graphqlServer.replace(/\/graphql$/, "");
      const url = `${baseUrl}/download/${documentId}`;
      const headers = {
        Authorization: `Bearer ${this.accessToken}`,
      };
      const headResp = await axios.head(url, { headers });
      const size = parseInt(headResp.headers["content-length"] || "0", 10);
      const chunkSize = 1024 * 5 * 1024; // 5MB
      let chunks = [];
      // Prepare all chunk ranges
      const ranges = [];
      for (let startByte = 0; startByte < size; startByte += chunkSize) {
        const endByte = Math.min(startByte + chunkSize - 1, size - 1);
        ranges.push({ startByte, endByte });
      }

      // Download all chunks in parallel
      const chunkPromises = ranges.map(({ startByte, endByte }) => {
        const rangeHeaders = {
          ...headers,
          Range: `bytes=${startByte}-${endByte}`,
        };
        return axios
          .get(url, {
            headers: rangeHeaders,
            responseType: "arraybuffer",
          })
          .then((resp) => {
            return new Uint8Array(resp.data);
          });
      });

      // Wait for all chunks to finish
      // Download chunks in batches of 8 at a time
      chunks = [];
      let batchSize = 8;
      for (let i = 0; i < chunkPromises.length; i += batchSize) {
        const batch = chunkPromises.slice(i, i + batchSize);
        const batchResults = await Promise.all(batch);
        chunks.push(...batchResults);
      }
      // Concatenate all Uint8Arrays
      let totalLength = chunks.reduce((acc, chunk) => acc + chunk.length, 0);
      let fileBuffer = new Uint8Array(totalLength);
      let offset = 0;
      for (let chunk of chunks) {
        fileBuffer.set(chunk, offset);
        offset += chunk.length;
      }
      const content = uint8ToBase64(fileBuffer);
      return { content: `data:application/pdf;base64,${content}` };
    } catch (error) {
      console.error("Download failed:", error);
      throw error;
    }
  }
}

      const  uint8ToBase64 = (uint8Array)=> {
        let binary = '';
        const chunkSize = 0x8000; // 32KB
        for (let i = 0; i < uint8Array.length; i += chunkSize) {
          binary += String.fromCharCode.apply(
            null,
            uint8Array.subarray(i, i + chunkSize)
          );
        }
        return btoa(binary);
      }

//TODO: move data formatters to a separate layer
const formatGetProjectsResponse = (response) => {
  const {
    projects: {
      edges = [],
      pageInfo: { hasNextPage = false, endCursor = null },
    },
  } = response;
  const payload = { hasNextPage, endCursor, projects: [] };
  edges.forEach((project) => {
    const {
      node: {
        id,
        name,
        description,
        publicStatus,
        createdAt,
        updatedAt,
        sessionUserRole = "OWNER",
        totalDocumentCount = 0,
        totalCollaboratorCount = 0,
        lastAccessedAt = 0,
        topCollaborators = [],
        ownerProfile = {},
        isBlocked = false,
      } = {},
    } = project;
    let permissions = [];
    ROLE_PERMISSIONS.some((p) => {
      const { role, permissions: value } = p;
      if (role === sessionUserRole) {
        permissions = value;
      }
    });
    const projectData = {
      id,
      name,
      description,
      publicStatus,
      createdAt,
      updatedAt,
      lastAccessedAt,
      permissions,
      userRole: sessionUserRole,
      totalDocuments: totalDocumentCount,
      totalCollaboratorCount,
      topCollaborators,
      ownerProfile,
      isBlocked
    };
    payload.projects.push(projectData);
  });
  return payload;
};

const formatGetProjectResponse = (response) => {
  const { project, sessionUserRole } = response;
  let projectPermissions = [];
  ROLE_PERMISSIONS.some((p) => {
    const { role, permissions } = p;
    if (role === project?.sessionUserRole) {
      projectPermissions = permissions;
      return true;
    }
  });
  // project.permissions = [...Object.values(COLLABORATOR_PERMISSIONS)];
  project.permissions = projectPermissions;
  project.userRole = project.sessionUserRole;
  return project;
};

const formatGetProjectCollaboratorsResponse = (response) => {
  return response;
};

const formatGetDocumentResponse = (response) => {
  const {
    project: {
      documents: {
        edges = [],
        pageInfo: { hasNextPage = false, endCursor = null },
      },
    } = {},
    projectId,
  } = response;
  const docs = edges.map((e) => {
    const { node: payload = {} } = e;
    const { preview = "", originMimeType = "application/pdf" } = payload;
    payload.preview = `data:image/png;base64,${preview}`;
    payload.origin = { mimeType: originMimeType };
    payload.projectId = projectId;
    return payload;
  });
  return { documents: docs, hasNextPage, endCursor };
};

// Each edge is a singleton project wrapping exactly one document — flatten
// down to a plain document list, carrying the project's id along as
// projectId (needed to route to /p/:projectId/d/:docId).
const formatSingletonProjectsResponse = (response) => {
  const { edges = [], pageInfo: { hasNextPage = false } = {} } = response?.singletonProjectDetails ?? {};
  const documents = edges
    .map((e) => {
      const project = e.node ?? {};
      const docNode = project.documents?.edges?.[0]?.node;
      if (!docNode) return null;
      const { preview = "" } = docNode;
      return {
        ...docNode,
        preview: preview ? `data:image/png;base64,${preview}` : "",
        projectId: project.id,
        projectUserRole: project.sessionUserRole,
        ownerProfile: project.ownerProfile,
        topCollaborators: project.topCollaborators,
        totalCollaboratorCount: project.totalCollaboratorCount,
      };
    })
    .filter(Boolean);
  return { documents, hasNextPage };
};
